import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Plus, Edit2, Trash2, GripVertical } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useEventTiming } from './EventTimingContext';
import { BREAK_LENGTH_OPTIONS } from './constants';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component
function SortableBreakRow({ breakItem, onEdit, onDelete, canEdit, canDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: breakItem.break_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-stone-100 hover:bg-stone-50">
      <td className="w-8 py-3 px-2">
        <button {...attributes} {...listeners} className="cursor-grab hover:text-green-700">
          <GripVertical className="h-4 w-4 text-stone-400" />
        </button>
      </td>
      <td className="py-3 px-2">{breakItem.break_type}</td>
      <td className="py-3 px-2">
        {breakItem.display_text || <span className="text-muted-foreground italic">Not displayed</span>}
      </td>
      <td className="py-3 px-2 text-center">{breakItem.length}</td>
      <td className="py-3 px-2 text-center">
        <div className="flex justify-center gap-2">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(breakItem)}
              data-testid={`edit-break-${breakItem.break_id}`}
            >
              <Edit2 className="h-4 w-4 text-[#D4A84B]" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(breakItem)}
              data-testid={`delete-break-${breakItem.break_id}`}
            >
              <Trash2 className="h-4 w-4 text-[#D4A84B]" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function CompetitionBreaksTab() {
  const {
    currentUser,
    isSuperUser,
    competitionBreaks,
    setCompetitionBreaks,
    fetchBreaks,
    saving,
    setSaving,
  } = useEventTiming();

  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState(null);
  const [newBreakType, setNewBreakType] = useState('');
  const [newBreakDisplayText, setNewBreakDisplayText] = useState('');
  const [newBreakLength, setNewBreakLength] = useState('0:30:00');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = competitionBreaks.findIndex(b => b.break_id === active.id);
      const newIndex = competitionBreaks.findIndex(b => b.break_id === over.id);
      
      const newOrder = arrayMove(competitionBreaks, oldIndex, newIndex);
      setCompetitionBreaks(newOrder);
      
      // Save new order to backend
      try {
        await axios.post(`${API}/competition-breaks/reorder`, {
          break_ids: newOrder.map(b => b.break_id)
        }, { withCredentials: true });
      } catch (error) {
        console.error('Error saving break order:', error);
        toast.error('Failed to save order');
        fetchBreaks(); // Revert on error
      }
    }
  };

  const handleSaveBreak = async () => {
    if (!newBreakType.trim()) {
      toast.error('Please enter a break type');
      return;
    }

    setSaving(true);
    try {
      if (editingBreak) {
        await axios.put(`${API}/competition-breaks/${editingBreak.break_id}`, {
          display_text: newBreakDisplayText,
          length: newBreakLength
        }, { withCredentials: true });
        toast.success('Break updated successfully');
      } else {
        await axios.post(`${API}/competition-breaks`, {
          break_type: newBreakType,
          display_text: newBreakDisplayText,
          length: newBreakLength
        }, { withCredentials: true });
        toast.success('Break created successfully');
      }
      setBreakDialogOpen(false);
      setEditingBreak(null);
      setNewBreakType('');
      setNewBreakDisplayText('');
      setNewBreakLength('0:30');
      fetchBreaks();
    } catch (error) {
      console.error('Error saving break:', error);
      toast.error(error.response?.data?.detail || 'Failed to save break');
    } finally {
      setSaving(false);
    }
  };

  const handleEditBreak = (breakItem) => {
    setEditingBreak(breakItem);
    setNewBreakType(breakItem.break_type);
    setNewBreakDisplayText(breakItem.display_text || '');
    setNewBreakLength(breakItem.length);
    setBreakDialogOpen(true);
  };

  const handleDeleteBreak = async (breakItem) => {
    if (!confirm(`Are you sure you want to delete "${breakItem.break_type}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/competition-breaks/${breakItem.break_id}`, { withCredentials: true });
      toast.success('Break deleted successfully');
      fetchBreaks();
    } catch (error) {
      console.error('Error deleting break:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete break');
    }
  };

  const handleAddNew = () => {
    setEditingBreak(null);
    setNewBreakType('');
    setNewBreakDisplayText('');
    setNewBreakLength('0:30');
    setBreakDialogOpen(true);
  };

  const canEditBreak = (breakItem) => {
    if (breakItem.is_system) {
      return isSuperUser;
    }
    return isSuperUser || breakItem.created_by === currentUser?.user_id;
  };

  const canDeleteBreak = (breakItem) => {
    if (isSuperUser) return true;
    if (breakItem.is_system) return false;
    return breakItem.created_by === currentUser?.user_id;
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Competition Breaks</h2>
        <Button onClick={handleAddNew} className="btn-primary" data-testid="add-break">
          <Plus className="mr-2 h-4 w-4" />
          Add Break Type
        </Button>
      </div>

      <Card className="p-6">
        {competitionBreaks.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No break types configured yet.</p>
            <p className="text-sm mt-1">Click "Add Break Type" to create your first entry.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="w-8 py-3 px-2"></th>
                    <th className="text-left py-3 px-2 font-semibold" style={{ color: '#2D4A3E' }}>Break Type</th>
                    <th className="text-left py-3 px-2 font-semibold" style={{ color: '#2D4A3E' }}>Display Text</th>
                    <th className="text-center py-3 px-2 font-semibold" style={{ color: '#2D4A3E', width: '150px' }}>Default Length (h:mm)</th>
                    <th className="text-center py-3 px-2 font-semibold" style={{ color: '#2D4A3E', width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <SortableContext items={competitionBreaks.map(b => b.break_id)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {competitionBreaks.map((breakItem) => (
                      <SortableBreakRow
                        key={breakItem.break_id}
                        breakItem={breakItem}
                        onEdit={handleEditBreak}
                        onDelete={handleDeleteBreak}
                        canEdit={canEditBreak(breakItem)}
                        canDelete={canDeleteBreak(breakItem)}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
          </div>
        )}
      </Card>

      <Dialog open={breakDialogOpen} onOpenChange={(val) => { 
        setBreakDialogOpen(val); 
        if (!val) { 
          setEditingBreak(null); 
          setNewBreakType(''); 
          setNewBreakDisplayText(''); 
          setNewBreakLength('0:30'); 
        } 
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBreak ? 'Edit Break Type' : 'Add Break Type'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Break Type *</Label>
              {editingBreak ? (
                <Input value={newBreakType} disabled className="mt-1 bg-muted" />
              ) : (
                <Input
                  value={newBreakType}
                  onChange={(e) => setNewBreakType(e.target.value)}
                  placeholder="Enter break type name"
                  className="mt-1"
                />
              )}
              {editingBreak && (
                <p className="text-xs text-muted-foreground mt-1">Break type name cannot be changed</p>
              )}
            </div>

            <div>
              <Label>Display Text</Label>
              <Input
                value={newBreakDisplayText}
                onChange={(e) => setNewBreakDisplayText(e.target.value)}
                placeholder="Text shown in programme (leave blank to hide)"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                If left blank, this break will be timed but not displayed in the programme
              </p>
            </div>

            <div>
              <Label>Default Length (h:mm)</Label>
              <Select value={newBreakLength} onValueChange={setNewBreakLength}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                  {BREAK_LENGTH_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setBreakDialogOpen(false); setEditingBreak(null); }}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveBreak}
                className="btn-primary"
                disabled={!newBreakType.trim() || saving}
              >
                {saving ? 'Saving...' : (editingBreak ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
